import { requireEnv } from '@virtual-care/shared'
import type { Session } from '@virtual-care/shared'

interface FetchOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
}

function getRoomIdFromEnvironment(): string | null {
  const urlParams = new URLSearchParams(window.location.search)
  const roomParam = urlParams.get('room')
  if (roomParam) {
    const normalizedRoom = roomParam.startsWith('room-') ? roomParam : `room-${roomParam}`
    localStorage.setItem('virtualcare-room-id', normalizedRoom)
    return normalizedRoom
  }
  return localStorage.getItem('virtualcare-room-id')
}

class ApiClient {
  private roomId: string | null = null
  private apiBaseUrl: string

  constructor() {
    this.apiBaseUrl = requireEnv(process.env.VUE_APP_API_BASE_URL, "VUE_APP_API_BASE_URL")
    const initialRoomId = getRoomIdFromEnvironment()
    if (initialRoomId) {
      this.roomId = initialRoomId
    } else {
      console.warn('[ApiClient] No room ID configured. Provide via URL: ?room=room-302')
    }
  }

  private async request<T>(endpoint: string, options: FetchOptions = {}): Promise<T> {
    if (!this.roomId) {
      throw new Error('Room ID not configured. Access the app with URL parameter: ?room=room-302')
    }
    const url = `${this.apiBaseUrl}/api${endpoint}`

    const defaultHeaders: HeadersInit = {
      'Content-Type': 'application/json',
      'x-user-id': this.roomId,
      'x-user-name': `Room ${this.roomId.replace('room-', '')}`,
      'x-user-role': 'patient',
    }

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...(options.headers as HeadersInit),
      },
      body: undefined,
    }

    if (options.body && typeof options.body === 'object') {
      config.body = JSON.stringify(options.body)
    }

    const response = await fetch(url, config)

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }))
      throw new Error(error.error || `HTTP ${response.status}`)
    }

    return response.json() as Promise<T>
  }

  setRoomId(roomId: string): void {
    if (!roomId) {
      throw new Error('Room ID cannot be empty')
    }
    this.roomId = roomId
  }

  getRoomId(): string {
    if (!this.roomId) {
      throw new Error('Room ID not configured')
    }
    return this.roomId
  }

  async getSession(sessionId: string) {
    return this.request<unknown>(`/sessions/${sessionId}`)
  }

  async joinSession(sessionId: string, role: 'nurse' | 'patient') {
    return this.request<{ success: boolean; session: Session }>(
      `/sessions/${sessionId}/join`,
      { method: 'POST', body: { role } }
    )
  }

  async declineSession(sessionId: string) {
    return this.request<unknown>(`/sessions/${sessionId}/decline`, { method: 'POST' })
  }

  async markSessionMissed(sessionId: string) {
    return this.request<unknown>(`/sessions/${sessionId}/missed`, { method: 'POST' })
  }

  async respondAvailability(sessionId: string, response: 'available' | 'not_now') {
    return this.request<{ success: boolean }>(`/sessions/${sessionId}/availability`, {
      method: 'POST',
      body: { response },
    })
  }

  async endSession(sessionId: string) {
    return this.request<unknown>(`/sessions/${sessionId}`, { method: 'DELETE' })
  }
}

export const apiClient = new ApiClient()
